import React, { useState, useEffect, Fragment, useCallback } from 'react';
import {
  Stack,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormGroup,
  Radio,
  TextField,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  RadioGroup,
  FormControlLabel,
  Alert,
  Box,
  Link,
  InputLabel,
} from '@mui/material';
import { FilePicker } from 'react-file-picker';

import useCustomerRequest from '../../../requests/CustomerRequest';
import useAdminRequest from '../../../requests/AdminRequest';

import RequestCampaignForm from './RequestCampaignForm';
import UpgradeTrialCustomerForm from './UpgradeTrialCustomerForm';
import ManagePoolNumber from '../../common/management/ManagePoolNumber';
import ManageDedicatedNumbers from '../../common/management/ManageDedicatedNumbers';
import CustomersFormattedMessages from '../../../messages/admin/CustomersFormattedMessages';
import AttachmentListComponent from './Attachments/AttachmentsTable';
import AttachmentListItem from './Attachments/AttachmentsTableRow';
import CommentListComponent from './Comments/CommentsTable';
import CommentListItem from './Comments/CommentsTableRow';
import PdfModal from '../../common/modals/PdfModal';
import CustomerEditForm from './CustomerEditForm';
import {
  KeyboardArrowDown,
  KeyboardArrowRight,
  Add,
} from '@mui/icons-material';
import Loader from '../../common/Loader';

const CustomerEdit = ({
  customer,
  backButtonClick,
  navigate,
  errorHandler,
  comments,
  setComments,
  attachments,
  setAttachments,
  refreshCustomer,
  isTransferDedicatedNumber,
  updateActingCustomerDetails,
  setHeaderButtons,
  managePoolNumbers,
  setManagePoolNumbers,
  manageDedicatedNumbers,
  setManageDedicatedNumbers,
}) => {
  const [isSavingComment, setIsSavingComment] = useState(false);
  const [isSavingAttachment, setIsSavingAttachment] = useState(false);
  const [customerStatus, setCustomerStatus] = useState('');
  const [toggleCampaign, setToggleCampaign] = useState(false);
  const [toggleNumbersView, setToggleNumbersView] = useState(false);
  const [edits, setEdits] = useState({});
  const [tariff_plans, setTariffPlans] = useState([]);
  const [class_types, setClassTypes] = useState([]);
  const [sales_agents, setSalesAgents] = useState(null);
  const [editableComments, setEditableComments] = useState([]);
  const [poolNumbers, setPoolNumbers] = useState(null);
  const [dedicatedNumbers, setDedicatedNumbers] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [file, setFile] = useState(null);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [showUpgradeTrialModal, setShowUpgradeTrialModal] = useState(false);
  const [customerAttachmentToDelete, setCustomerAttachmentToDelete] =
    useState(null);
  const [showRequestCampaignModal, setShowRequestCampaignModal] =
    useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [uploadType, setUploadType] = useState('O');
  const [errorText, setErrorText] = useState(null);
  const [error, setError] = useState(null);
  const [addNewAttachmentSelected, setAddNewAttachmentSelected] =
    useState(false);
  const [addNewCommentSelected, setAddNewCommentSelected] = useState(false);
  const { getCustomerExtras, getAllNumbers } = useCustomerRequest();
  const {
    getCustomerComments,
    getCustomerAttachments,
    getPdf,
    downloadCustomerAttachment,
    addCustomerAttachments,
    addCustomerComments,
    deleteCustomerAttachment,
    deleteCustomerComments,
    editCustomerComments,
  } = useAdminRequest();

  const updateCustomerDetails = useCallback(async () => {
    setHeaderButtons(generateLander);

    const [results] = await Promise.all([
      getCustomerExtras(errorHandler),
      updateActingCustomerDetails(customer.customer_id),
    ]);

    if (isTransferDedicatedNumber) {
      console.log('isTransferDedicatedNumber');
      toggleChevronClick('toggleNumbersView', handleGetAllNumbers);
    }
    setTariffPlans(results?.tariff_plans ?? []);
    setClassTypes(results?.class_types);
    setSalesAgents(results?.sales_agents);
    setCustomerStatus(customer.customer_status);
  }, []);

  useEffect(() => {
    updateCustomerDetails();
  }, [updateCustomerDetails]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === 'uploadType') setUploadType(value);
    else if (name === 'newComment') setNewComment(value);
    else {
      setEdits((prevEdits) => ({
        ...prevEdits,
        [name]: value,
      }));
    }
  };

  const callBack = () => {
    setPoolNumbers(null);
    setDedicatedNumbers(null);
    setManageDedicatedNumbers(false);
    handleGetAllNumbers();
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };

  const showConfirmationModal = (customerAttachmentId) => {
    setShowConfirmation(true);
    setCustomerAttachmentToDelete(customerAttachmentId);
  };

  const showConfirmationDialog = () => {
    return (
      <Dialog
        open={showConfirmation}
        onClose={handleCloseConfirmation}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle variant="h2" id="alert-dialog-title">
          Deletion Confirmation
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you would like to delete the attachments?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleCloseConfirmation}
            autoFocus
          >
            No
          </Button>

          <Button
            variant="contained"
            color="secondary"
            onClick={() => deleteAttachmentRequest(customerAttachmentToDelete)}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const fetchComments = async () => {
    const comments = await getCustomerComments(
      customer.customer_id,
      errorHandler
    );
    console.log(comments);
    setComments(comments);
  };

  const fetchAttachments = async () => {
    const attachments = await getCustomerAttachments(
      customer.customer_id,
      errorHandler
    );
    setAttachments(attachments);
  };

  const downloadAddendum = async () => {
    setShowPdfModal(true);

    const response = await getPdf(
      'customer',
      null,
      customer.customer_id,
      errorHandler
    );

    if (!response) {
      setError(response.statusText);
    }

    setPdfBlob(response);
  };

  const generateLander = () => {
    let upgradeTrialButton;

    if (customer.trialUpgradeStatus === 'NeedsCampaign') {
      upgradeTrialButton = (
        <Button
          variant="contained"
          color="secondary"
          onClick={() => setShowRequestCampaignModal(true)}
        >
          Request Campaign
        </Button>
      );
    } else if (customer.trialUpgradeStatus === 'ReadyForUpgrade') {
      upgradeTrialButton = (
        <Button
          variant="contained"
          color="secondary"
          onClick={() => setShowUpgradeTrialModal(true)}
        >
          Upgrade Trial
        </Button>
      );
    } else if (customer.trialUpgradeStatus === 'PendingCampaign') {
      upgradeTrialButton = (
        <Button variant="contained" color="secondary" disabled>
          Campaign being processed
        </Button>
      );
    }

    return (
      <Stack gap={2} flexDirection={{ md: 'row' }}>
        <Box>
          <Button
            variant="contained"
            color="secondary"
            onClick={() =>
              navigate(`/admin/customers/${customer.customer_id}/profile`)
            }
          >
            {CustomersFormattedMessages.viewProfile}
          </Button>
        </Box>
        <Box>
          <Button
            variant="contained"
            color="secondary"
            onClick={downloadAddendum}
          >
            {CustomersFormattedMessages.viewAddendum}
          </Button>
        </Box>
        <Box>
          <Button
            variant="contained"
            color="secondary"
            onClick={() =>
              navigate('/billing/invoices', {
                state: { customerName: customer?.customer_name },
              })
            }
          >
            {CustomersFormattedMessages.viewInvoices}
          </Button>
        </Box>
        <Box>{upgradeTrialButton}</Box>
      </Stack>
    );
  };

  const generatePoolNumbersTable = () => {
    const htmlRows = [];
    let poolNumbersContent = <Loader />;

    if (poolNumbers) {
      if (poolNumbers.length === 0)
        poolNumbersContent = (
          <Typography gutterBottom>
            No pool numbers found for this customer
          </Typography>
        );
      else {
        poolNumbers.forEach((item, i) => {
          if (!item.vendor && item.route_name) {
            const splitString = item.route_name.split(' ');
            item.vendor = splitString[0];
          }

          htmlRows.push(
            <TableRow key={i}>
              <TableCell>{i + 1}</TableCell>
              <TableCell>
                <Link
                  tabIndex={0}
                  onClick={() =>
                    navigate('/admin/service-config', {
                      state: { devicesRedirect: item.device_id },
                    })
                  }
                >
                  {item.device_id}
                </Link>
              </TableCell>
              <TableCell>{item.device_name}</TableCell>
              <TableCell>{item.device_status}</TableCell>
              <TableCell>{item.reply_tn}</TableCell>
              <TableCell>{item.messaging_service}</TableCell>
              <TableCell>{item.campaign_code}</TableCell>
              <TableCell>{item.vendor}</TableCell>
              <TableCell></TableCell>
            </TableRow>
          );
        });

        poolNumbersContent = (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead sx={{ whiteSpace: 'nowrap' }}>
                <TableRow>
                  <TableCell style={{ width: '3%' }}>Row</TableCell>
                  <TableCell style={{ width: '5%' }}>Device ID</TableCell>
                  <TableCell style={{ width: '31%' }}>Device Name</TableCell>
                  <TableCell style={{ width: '8%' }}>Device Status</TableCell>
                  <TableCell style={{ width: '10%' }}>Reply TN</TableCell>
                  <TableCell style={{ width: '20%' }}>
                    Messaging Service
                  </TableCell>
                  <TableCell style={{ width: '8%' }}>Campaign ID</TableCell>
                  <TableCell style={{ width: '7%' }}>Gateway</TableCell>
                  <TableCell style={{ width: '8%' }}>
                    Forward Calls to
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{htmlRows}</TableBody>
            </Table>
          </TableContainer>
        );
      }
    }

    return (
      <Collapse in={toggleNumbersView}>
        <Stack gap={2}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h3">
              {CustomersFormattedMessages.poolNumbers}
            </Typography>

            <Button
              variant="contained"
              color="secondary"
              onClick={() => setManagePoolNumbers(true)}
            >
              {CustomersFormattedMessages.managePoolNumbers}
            </Button>
          </Box>

          <Box>{poolNumbersContent}</Box>
        </Stack>
      </Collapse>
    );
  };

  const generateDedicatedNumbersTable = () => {
    const dedicatedNumbersContent = [];
    if (dedicatedNumbers) {
      if (dedicatedNumbers.length === 0)
        dedicatedNumbersContent.push(
          <Typography gutterBottom>
            No dedicated numbers found for this customer
          </Typography>
        );
      else {
        dedicatedNumbers.forEach((user, i) => {
          if (!user.numbers) return;

          const htmlRows = [];

          user.numbers.forEach((device, j) => {
            if (!device.vendor && device.route_name) {
              const splitString = device.route_name.split(' ');
              device.vendor = splitString[0];
            }

            htmlRows.push(
              <TableRow key={j} style={{ textAlign: 'center' }}>
                <TableCell>{j + 1}</TableCell>
                <TableCell>
                  <Link
                    tabIndex={0}
                    onClick={() =>
                      navigate('/admin/service-config', {
                        state: { devicesRedirect: device.device_id },
                      })
                    }
                  >
                    {device.device_id}
                  </Link>
                </TableCell>
                <TableCell>{device.device_name}</TableCell>
                <TableCell>{device.device_status}</TableCell>
                <TableCell>{device.reply_tn}</TableCell>
                <TableCell>{device.messaging_service}</TableCell>
                <TableCell>{device.campaign_code}</TableCell>
                <TableCell>{device.vendor}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            );
          });

          dedicatedNumbersContent.push(
            <Box key={i}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'end',
                  marginBottom: 2,
                }}
              >
                <Typography fontWeight={500}>{user.user_email}</Typography>

                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    setManageDedicatedNumbers(true);
                    setSelectedUser({
                      customer_user_id: user.customer_user_id,
                      customer_id: customer.customer_id,
                    });
                  }}
                >
                  {CustomersFormattedMessages.manageDedicatedNumbers}
                </Button>
              </Box>

              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead sx={{ whiteSpace: 'nowrap' }}>
                    <TableRow>
                      <TableCell style={{ width: '3%' }}>Row</TableCell>
                      <TableCell style={{ width: '5%' }}>Device ID</TableCell>
                      <TableCell style={{ width: '31%' }}>
                        Device Name
                      </TableCell>
                      <TableCell style={{ width: '8%' }}>
                        Device Status
                      </TableCell>
                      <TableCell style={{ width: '10%' }}>Reply TN</TableCell>
                      <TableCell style={{ width: '20%' }}>
                        Messaging Service
                      </TableCell>
                      <TableCell style={{ width: '8%' }}>Campaign ID</TableCell>
                      <TableCell style={{ width: '7%' }}>Gateway</TableCell>
                      <TableCell style={{ width: '8%' }}>
                        Forward Calls to
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>{htmlRows}</TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        });
      }
    }

    return (
      <Collapse in={toggleNumbersView}>
        <Box>
          <Typography variant="h3" gutterBottom>
            {CustomersFormattedMessages.dedicatedNumbers}
          </Typography>

          <Stack gap={2}>{dedicatedNumbersContent}</Stack>
        </Box>
      </Collapse>
    );
  };

  const toggleChevronClick = async (field, fetchCallback) => {
    if (field === 'toggleNumbersView') setToggleNumbersView(!toggleNumbersView);
    else if (field === 'toggleCampaign') setToggleCampaign(!toggleCampaign);
    fetchCallback();
  };

  const handleGetAllNumbers = async () => {
    if (!poolNumbers && customer) {
      const { poolNumbers, dedicatedNumbers } = await getAllNumbers(
        customer.customer_id,
        errorHandler
      );

      console.log('pool numbers:', poolNumbers);
      console.log('dedicated numbers:', dedicatedNumbers);

      setPoolNumbers(poolNumbers);
      setDedicatedNumbers(dedicatedNumbers);
    }
  };

  const generateAllNumbersSection = () => {
    return (
      <Fragment>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h2">
            {CustomersFormattedMessages.manageAllNumbers}
          </Typography>

          <Button
            startIcon={
              toggleNumbersView ? <KeyboardArrowDown /> : <KeyboardArrowRight />
            }
            variant="link"
            color="secondary"
            onClick={() =>
              toggleChevronClick('toggleNumbersView', handleGetAllNumbers)
            }
          >
            Show All Numbers
          </Button>
        </Box>
      </Fragment>
    );
  };

  const generateCustomerAttachmentsTable = () => {
    let list = formatAttachmentData(attachments);
    if (isSavingAttachment)
      return (
        <Stack gap={2}>
          {addNewAttachment()}
          <Typography>Saving...</Typography>
        </Stack>
      );

    if (attachments.length === 0) {
      return (
        <Stack gap={2}>
          {addNewAttachment()}
          <Typography>{CustomersFormattedMessages.noResultsFound}</Typography>
        </Stack>
      );
    } else {
      return (
        <Stack gap={2}>
          {addNewAttachment()}
          <AttachmentListComponent list={list} />
        </Stack>
      );
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const openAttachment = async (attachmentId, attachmentName) => {
    await downloadCustomerAttachment(
      customer.customer_id,
      attachmentId,
      attachmentName,
      errorHandler
    );
  };

  const formatCommentData = (comments) => {
    return comments?.map((item) => {
      if (item)
        return (
          <CommentListItem
            key={'key_' + item.customer_comments_id}
            commentId={item.customer_comments_id}
            customerId={item.customer_id}
            createdDate={item.created_at}
            contactName={item.customer_contact_name}
            comment={item.customer_comments}
            onClickEdit={getEditComment}
            onClickDelete={removeComment}
            formatDate={formatDate}
            editableComments={editableComments}
            updateComment={updateComment}
            item={item}
          />
        );
      return null;
    });
  };

  const formatAttachmentData = (attachments) => {
    return attachments.map((item) => {
      if (item)
        return (
          <AttachmentListItem
            key={'key_' + item.customer_attachment_id}
            customer_id={item.customer_id}
            customer_attachment_id={item.customer_attachment_id}
            created_at={item.created_at}
            customer_contact_name={item.customer_contact_name}
            attachment_name={item.attachment_name + item.attachment_file_type}
            status_flag={item.status_flag === 'A' ? 'Active' : 'Inactive'}
            updated_at={item.updated_at}
            onClickDelete={showConfirmationModal}
            onClickOpen={openAttachment}
            formatDate={formatDate}
          />
        );
      return null;
    });
  };

  const selectAddNewButtons = (target) => {
    if (target === 'comment') {
      setAddNewCommentSelected(true);
    } else if (target === 'attachment') {
      setAddNewAttachmentSelected(true);
    }
  };

  const deselectAddNewButtons = (target) => {
    if (target === 'comment') {
      setAddNewCommentSelected(false);
    } else if (target === 'attachment') {
      setAddNewAttachmentSelected(false);
      setFile(null);
      setErrorText(null);
    }
  };

  const submitNewAttachment = () => {
    uploadAttachment();
  };

  const uploadAttachment = async () => {
    try {
      setIsSavingAttachment(true);
      let type = uploadType;

      if (!file || typeof file === 'undefined') {
        // Handle no file error
        setErrorText('Must select a valid file to upload');
        setIsSavingAttachment(false);
      } else {
        deselectAddNewButtons('attachment');
        // Import Contacts request
        await addCustomerAttachments(
          customer.customer_id,
          type,
          file,
          errorHandler
        );

        await fetchAttachments();
        setFile(null);
      }
      setIsSavingAttachment(false);
    } catch (e) {
      // Handle Upload Error
      setErrorText(e.message);
      setIsSavingAttachment(false);
    }
  };

  const deleteAttachmentRequest = async (attachmentId) => {
    try {
      handleCloseConfirmation();
      // Delete Customer Attachment
      await deleteCustomerAttachment(
        customer.customer_id,
        attachmentId,
        errorHandler
      );
      await fetchAttachments();

      setErrorText(null);
      setShowConfirmation(false);
    } catch (e) {
      // Handle Delete Error
      setErrorText(e.message);
      setShowConfirmation(false);
    }
  };

  const addNewComment = () => {
    if (addNewCommentSelected) {
      return (
        <Stack gap={2}>
          <Typography variant="h2">
            {CustomersFormattedMessages.listOfComments}{' '}
          </Typography>

          <Box component="form" onSubmit={() => submitNewComment()}>
            <FormGroup>
              <TextField
                type="text"
                value={newComment}
                onChange={handleChange}
                id="newComment"
                label="New Comment"
                name="newComment"
                variant="outlined"
                size="small"
              />
            </FormGroup>
          </Box>

          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => deselectAddNewButtons('comment')}
            >
              {CustomersFormattedMessages.cancel}
            </Button>

            <Button
              variant="contained"
              color="secondary"
              onClick={() => submitNewComment()}
            >
              {CustomersFormattedMessages.save}
            </Button>
          </Box>
        </Stack>
      );
    } else {
      return (
        <Box justifyContent={'space-between'} display={'flex'}>
          <Typography variant="h2">
            {CustomersFormattedMessages.listOfComments}
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Add />}
            onClick={() => selectAddNewButtons('comment')}
          >
            {CustomersFormattedMessages.addComment}
          </Button>
        </Box>
      );
    }
  };

  const generateFileSelect = () => {
    return (
      <Stack gap={2} sx={{ marginBottom: 2 }}>
        <FormGroup>
          <InputLabel id="upload-type-radio-group-label">
            {CustomersFormattedMessages.uploadType}
          </InputLabel>
          <RadioGroup
            aria-labelledby="upload-type-radio-group-label"
            defaultValue="O"
            id="uploadType"
            name="uploadType"
            value={uploadType}
            onChange={handleChange}
          >
            <FormControlLabel
              value="T"
              control={<Radio />}
              label={CustomersFormattedMessages.tariffConfirmation}
              sx={{ marginBottom: 0 }}
            />
            <FormControlLabel
              value="P"
              control={<Radio />}
              label={CustomersFormattedMessages.primaryContactDetails}
              sx={{ marginBottom: 0 }}
            />
            <FormControlLabel
              value="M"
              control={<Radio />}
              label={CustomersFormattedMessages.meetingMinutes}
              sx={{ marginBottom: 0 }}
            />
            <FormControlLabel
              value="O"
              control={<Radio />}
              label={CustomersFormattedMessages.other}
              sx={{ marginBottom: 0 }}
            />
          </RadioGroup>
        </FormGroup>

        <Box>
          <Typography gutterBottom>
            {CustomersFormattedMessages.selectTheFileToImport}
          </Typography>

          <FilePicker
            onChange={(fileObject) => captureFile(fileObject)}
            onError={(errMsg) => setErrorText(errMsg)}
          >
            <Button variant="contained" color="secondary">
              {CustomersFormattedMessages.chooseFile}
            </Button>
          </FilePicker>
          {file ? `Selected File: ${file.name}` : ''}
        </Box>
      </Stack>
    );
  };

  const generateFileSelectError = () => {
    if (!errorText) return null;

    return <Alert severity="error">{errorText}</Alert>;
  };

  const captureFile = (file) => {
    setFile(file);
    setErrorText('');
  };

  const addNewAttachment = () => {
    if (addNewAttachmentSelected) {
      return (
        <Stack gap={2}>
          <Typography variant="h2">
            {CustomersFormattedMessages.attachments}
          </Typography>
          {generateFileSelect()}

          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => deselectAddNewButtons('attachment')}
            >
              {CustomersFormattedMessages.cancel}
            </Button>

            <Button
              variant="contained"
              color="secondary"
              onClick={() => submitNewAttachment()}
            >
              {CustomersFormattedMessages.save}
            </Button>
          </Box>
          {generateFileSelectError()}
        </Stack>
      );
    } else {
      return (
        <Box justifyContent={'space-between'} display={'flex'}>
          <Typography variant="h2">
            {CustomersFormattedMessages.attachments}
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<Add />}
            onClick={() => selectAddNewButtons('attachment')}
          >
            {CustomersFormattedMessages.addAttachment}
          </Button>
        </Box>
      );
    }
  };

  const generateCustomerCommentsTable = () => {
    let list = formatCommentData(comments);
    if (isSavingComment)
      return (
        <Stack gap={2}>
          {addNewComment()}
          <Typography>Saving...</Typography>
        </Stack>
      );

    if (comments?.length === 0) {
      return (
        <Stack gap={2}>
          {addNewComment()}
          <Typography>{CustomersFormattedMessages.noResultsFound}</Typography>
        </Stack>
      );
    } else {
      return (
        <Stack gap={2}>
          {addNewComment()}
          <CommentListComponent list={list} />
        </Stack>
      );
    }
  };

  const updateComment = (selectedItem, newComment) => {
    editCommentRequest(selectedItem.customer_comments_id, newComment);
  };

  const removeComment = (selectedItem) => {
    deleteComment(selectedItem.customer_comments_id);
  };

  const submitNewComment = () => {
    deselectAddNewButtons('comment');
    uploadNewComment();
  };

  const uploadNewComment = async () => {
    try {
      setIsSavingComment(true);
      await addCustomerComments(customer.customer_id, newComment, errorHandler);
      await fetchComments();

      setNewComment('');
      setErrorText(null);
      setIsSavingComment(false);
    } catch (e) {
      // Handle Upload Error
      setErrorText(e.message);
      setIsSavingComment(false);
    }
  };

  const deleteComment = async (commentId) => {
    try {
      await deleteCustomerComments(
        customer.customer_id,
        commentId,
        errorHandler
      );
      await fetchComments();
      setNewComment('');
      setErrorText(null);
    } catch (e) {
      // Handle Upload Error
      setErrorText(e.message);
    }
  };

  const editCommentRequest = async (commentId, newComment) => {
    try {
      await editCustomerComments(
        customer.customer_id,
        commentId,
        newComment,
        errorHandler
      );
      await fetchComments();

      setNewComment('');
      setErrorText(null);
      setEditableComments([]);
      return true;
    } catch (e) {
      // Handle Upload Error
      setErrorText(e.message);
    }
  };

  const getEditComment = (targetComment) => {
    setEditableComments((prevEditableComments) => {
      if (prevEditableComments.includes(targetComment.customer_comments_id)) {
        //if selected comment exists in array remove it
        return prevEditableComments.filter(
          (id) => id !== targetComment.customer_comments_id
        );
      } else {
        // else add the comment to the array
        return [...prevEditableComments, targetComment.customer_comments_id];
      }
    });
  };

  // Manage Pool Numbers
  if (managePoolNumbers) {
    return (
      <ManagePoolNumber
        callBack={callBack}
        customer={customer}
        errorHandler={errorHandler}
      />
    );
  }

  // Manage Dedicated Numbers
  if (manageDedicatedNumbers) {
    return (
      <ManageDedicatedNumbers
        callBack={callBack}
        user={selectedUser}
        errorHandler={errorHandler}
        page="customer"
      />
    );
  }

  return (
    <Box>
      <Stack gap={8}>
        <CustomerEditForm
          navigate={navigate}
          customer={customer}
          backButtonClick={backButtonClick}
          errorHandler={errorHandler}
          tariff_plans={tariff_plans}
          class_types={class_types}
          sales_agents={sales_agents}
          customerStatus={customerStatus}
          toggleCampaign={toggleCampaign}
          toggleNumbersView={toggleNumbersView}
          setToggleCampaign={setToggleCampaign}
          setToggleNumbersView={setToggleNumbersView}
        />
        <Stack gap={2}>
          {generateAllNumbersSection()}
          {generatePoolNumbersTable()}
          {generateDedicatedNumbersTable()}
        </Stack>

        <Box>{generateCustomerCommentsTable()}</Box>

        <Box>
          {generateCustomerAttachmentsTable()}
          {showConfirmationDialog()}
        </Box>
      </Stack>

      <PdfModal
        title="Addendum"
        blob={pdfBlob}
        show={showPdfModal}
        onHide={() => {
          setShowPdfModal(false);
          setPdfBlob(null);
        }}
      />
      <RequestCampaignForm
        showRequestCampaignModal={showRequestCampaignModal}
        hideModal={() => setShowRequestCampaignModal(false)}
        errorHandler={errorHandler}
        refreshCustomer={refreshCustomer}
        customerId={customer.customer_id}
        customerName={customer.customer_name}
        customerAccountNo={customer.customer_account_no}
      />
      <UpgradeTrialCustomerForm
        showUpgradeTrialModal={showUpgradeTrialModal}
        hideModal={() => setShowUpgradeTrialModal(false)}
        errorHandler={errorHandler}
        refreshCustomer={refreshCustomer}
        customerId={customer.customer_id}
      />
    </Box>
  );
};

export default CustomerEdit;
